import { Dialog, DialogContent, DialogTitle, DialogActions, Button, Typography, Stack, Box } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';

export interface Notification {
  id: string;
  title: string;
  content: string;
  type: 'info' | 'success' | 'warning' | 'error';
  priority: 'high' | 'medium' | 'low';
  createdAt: string;
  publisher?: string;
}

interface NotificationDialogProps {
  open: boolean;
  notifications: Notification[];
  onClose: () => void;
}

const TYPE_CONFIG: Record<string, { icon: typeof InfoOutlinedIcon; color: string }> = {
  info: { icon: InfoOutlinedIcon, color: '#3b82f6' },
  success: { icon: CheckCircleOutlineRoundedIcon, color: '#22c55e' },
  warning: { icon: WarningAmberRoundedIcon, color: '#f59e0b' },
  error: { icon: ErrorOutlineRoundedIcon, color: '#ef4444' },
};

export default function NotificationDialog({ open, notifications, onClose }: NotificationDialogProps) {
  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      slotProps={{ paper: { sx: { borderRadius: 2 } } }}
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 2, fontWeight: 600 }}>
        系统通知
      </DialogTitle>
      <DialogContent sx={{ py: 3, maxHeight: 400, overflowY: 'auto' }}>
        {notifications.length === 0 ? (
          <Typography variant="body1" sx={{ textAlign: 'center', color: 'text.secondary' }}>
            暂无通知
          </Typography>
        ) : (
          <Stack spacing={2}>
            {notifications.map((notification) => {
              const config = TYPE_CONFIG[notification.type] || TYPE_CONFIG.info;
              const IconComponent = config.icon;
              return (
                <Box
                  key={notification.id}
                  sx={{
                    border: '1px solid',
                    borderColor: config.color,
                    borderRadius: 1.5,
                    p: 2.5,
                    bgcolor: `${config.color}08`,
                  }}
                >
                  <Stack spacing={1}>
                    <Stack direction="row" sx={{ alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ width: 20, height: 20, borderRadius: 0.5, bgcolor: config.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <IconComponent sx={{ fontSize: 14, color: '#fff' }} />
                      </Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1 }}>
                        {notification.title}
                      </Typography>
                      {notification.publisher && (
                        <Typography variant="caption" sx={{ color: 'text.disabled', bgcolor: 'background.default', px: 1.5, py: 0.25, borderRadius: 1 }}>
                          {notification.publisher}
                        </Typography>
                      )}
                    </Stack>
                    <Typography variant="body2" sx={{ color: 'text.primary', pl: 6.5 }}>
                      {notification.content}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.disabled', pl: 6.5 }}>
                      {new Date(notification.createdAt).toLocaleString('zh-CN')}
                    </Typography>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 4, pb: 4, justifyContent: 'center' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={onClose}
          sx={{
            minWidth: 200,
            height: 44,
            borderRadius: 1,
            fontWeight: 600,
            textTransform: 'none',
          }}
        >
          我知道了
        </Button>
      </DialogActions>
    </Dialog>
  );
}